import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Button, Card, Col, Empty, Input, Modal, Pagination, Row, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography } from "antd";
import { CalendarOutlined, EditOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { helper } from "@src/controls/controlHelper";
import Loader from "@src/components/Loading";
import FormCtrl from "@src/controls/layouts/schemaTemplate/FormCtrl";

const { TabPane } = Tabs as any;
const { Text } = Typography;

// PAGE_ID for Match listing
const PAGE_ID = 98;
// Modal page for create/edit Match
const MODAL_PAGE_ID = 99;

type YearItem = number | string;

interface DictionaryItem {
  id: number;
  name?: string;
}

interface MatchItem {
  id: number;
  name?: string;
  year?: number;
  tournamentId?: number;
  teamA?: number;
  teamB?: number;
  footballSquadId?: number;
  stadiumId?: number;
  ballTimeText?: string | null;
  [key: string]: any;
}

const MatchCtrl: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [pageInfo, setPageInfo] = useState<any>(null);
  const [years, setYears] = useState<YearItem[]>([]);
  const [activeYear, setActiveYear] = useState<YearItem | undefined>();
  const [yearToMatches, setYearToMatches] = useState<Record<string, MatchItem[]>>({});
  const [yearToTotal, setYearToTotal] = useState<Record<string, number>>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(12);
  const [selectedTournament, setSelectedTournament] = useState<number | undefined>();
  const [showUpcomingDraft, setShowUpcomingDraft] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");
  const [activeKeyword, setActiveKeyword] = useState<string>("");
  const [activeTournament, setActiveTournament] = useState<number | undefined>();
  const [activeUpcoming, setActiveUpcoming] = useState<boolean>(false);

  // separate search states
  const [idKeyword, setIdKeyword] = useState<string>("");
  const [activeIdKeyword, setActiveIdKeyword] = useState<string>("");

  // tournament option pagination
  const [tourPage, setTourPage] = useState<number>(1);
  const [tourPageSize] = useState<number>(20);
  const [tourTotal, setTourTotal] = useState<number>(0);

  // dictionaries
  const [tournaments, setTournaments] = useState<DictionaryItem[]>([]);
  const [squads, setSquads] = useState<DictionaryItem[]>([]);
  const [teams, setTeams] = useState<DictionaryItem[]>([]);
  const [stadiums, setStadiums] = useState<DictionaryItem[]>([]);

  // modal state
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [modalQuery, setModalQuery] = useState<any>({});

  const dictionariesById = useMemo(() => {
    const toMap = (arr: DictionaryItem[]) => arr.reduce((acc: Record<number, DictionaryItem>, cur) => { acc[cur.id] = cur; return acc; }, {});
    return {
      tournament: toMap(tournaments),
      squad: toMap(squads),
      team: toMap(teams),
      stadium: toMap(stadiums),
    };
  }, [tournaments, squads, teams, stadiums]);

  const openCreate = useCallback(() => {
    setModalQuery({ page: MODAL_PAGE_ID, mode: "create" });
    setIsShowModal(true);
  }, []);

  const openEdit = useCallback((matchId: number) => {
    setModalQuery({ page: MODAL_PAGE_ID, mode: "edit", id: matchId });
    setIsShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsShowModal(false);
    // reload active year data after closing
    if (activeYear !== undefined) {
      // re-fetch matches for current year
      void loadMatches(String(activeYear));
    }
  }, [activeYear]);

  const loadYears = useCallback(async (_pageInfo: any) => {
    const rs: any = await helper.callPageApi(_pageInfo, "get_year", {});
    const data = rs?.data?.data ?? rs?.data ?? [];
    const ys: YearItem[] = Array.isArray(data) ? data : [];
    setYears(ys);
    if (ys.length > 0) setActiveYear(ys[0]);
  }, []);

  const normalizeList = (rs: any): any[] => {
    const d = rs?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  };

  const loadDictionaries = useCallback(async (_pageInfo: any) => {
    const [rt, rsq, rtm, rsd] = await Promise.all([
      helper.callPageApi(_pageInfo, "find_tournament", {}),
      helper.callPageApi(_pageInfo, "find_footballsquad", {}),
      helper.callPageApi(_pageInfo, "find_footballteam", {}),
      helper.callPageApi(_pageInfo, "find_stadium", {}),
    ]);
    setTournaments(normalizeList(rt) as DictionaryItem[]);
    setSquads(normalizeList(rsq) as DictionaryItem[]);
    setTeams(normalizeList(rtm) as DictionaryItem[]);
    setStadiums(normalizeList(rsd) as DictionaryItem[]);
  }, []);

  const loadMatches = useCallback(async (yearKey: string) => {
    const _pageInfo = pageInfo || (await helper.getPage(PAGE_ID));
    const filter: any = { year: Number(yearKey) };
    if (activeTournament) filter.tournamentId = activeTournament;
    if (activeUpcoming) filter.isOver = false;
    if (activeKeyword) {
      filter.name = { contains: activeKeyword };
    }
    if (activeIdKeyword) {
      const idNum = Number(activeIdKeyword);
      if (!isNaN(idNum) && idNum > 0) {
        filter.id = idNum;
      }
    }
    const rs: any = await helper.callPageApi(_pageInfo, "find", {
      queryInput: JSON.stringify(filter),
      limit: pageSize,
      skip: pageSize * (currentPage - 1),
      sort: JSON.stringify([{ ballTime: "desc" }]),
    });
    const list: MatchItem[] = rs?.data?.data ?? [];
    const total = Number(rs?.data?.count ?? 0);
    setYearToMatches((prev) => ({ ...prev, [yearKey]: list }));
    setYearToTotal((prev) => ({ ...prev, [yearKey]: total }));
  }, [pageInfo, activeTournament, activeUpcoming, activeKeyword, activeIdKeyword, currentPage, pageSize]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const _pageInfo = await helper.getPage(PAGE_ID);
        setPageInfo(_pageInfo);
        await Promise.all([loadYears(_pageInfo), loadDictionaries(_pageInfo)]);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadYears, loadDictionaries]);

  useEffect(() => {
    if (activeYear !== undefined) {
      void loadMatches(String(activeYear));
    }
  }, [activeYear, loadMatches]);

  const onApplyAllFilters = () => {
    setActiveKeyword(keyword.trim());
    setActiveIdKeyword(idKeyword.trim());
    setActiveTournament(selectedTournament);
    setActiveUpcoming(showUpcomingDraft);
    setCurrentPage(1);
    if (activeYear !== undefined) void loadMatches(String(activeYear));
  };

  const resetTournamentOptions = () => {
    setTournaments([]);
    setTourPage(1);
    setTourTotal(0);
  };

  const loadTournamentOptions = async (pageNum: number) => {
    const _pageInfo = pageInfo || (await helper.getPage(PAGE_ID));
    const rs: any = await helper.callPageApi(_pageInfo, "find_tournament", {
      limit: tourPageSize,
      skip: tourPageSize * (pageNum - 1),
      sort: JSON.stringify([{ id: "desc" }]),
    });
    const list: any[] = Array.isArray(rs?.data?.data) ? rs.data.data : [];
    const total = Number(rs?.data?.count ?? 0);
    setTournaments((prev) => (pageNum === 1 ? list : [...prev, ...list]));
    setTourTotal(total);
    setTourPage(pageNum);
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (id: number) => <Text type="secondary">#{id}</Text>,
    },
    {
      title: "Trận đấu",
      key: "match",
      render: (_: any, m: MatchItem) => {
        const home = dictionariesById.team[m.teamA || 0]?.name;
        const away = dictionariesById.team[m.teamB || 0]?.name;
        const homeImg = (dictionariesById.team[m.teamA || 0] as any)?.image;
        const awayImg = (dictionariesById.team[m.teamB || 0] as any)?.image;
        return (
          <Space direction="vertical" size={4}>
            <Space size={8}>
              <Avatar size={24} src={homeImg} shape="circle">{(home || "").charAt(0)}</Avatar>
              <Text strong style={{ fontSize: 14 }}>{home || "-"}</Text>
            </Space>
            <Space size={8}>
              <Avatar size={24} src={awayImg} shape="circle">{(away || "").charAt(0)}</Avatar>
              <Text strong style={{ fontSize: 14 }}>{away || "-"}</Text>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Tỉ số",
      key: "score",
      align: "center" as const,
      width: 120,
      render: (_: any, m: MatchItem) => {
        if (typeof m.scoreGoalTeamA === "number" && typeof m.scoreGoalTeamB === "number") {
          return <Text strong style={{ fontSize: 18 }}>{m.scoreGoalTeamA} - {m.scoreGoalTeamB}</Text>;
        }
        return <Tag color="processing">Upcoming</Tag>;
      },
    },
    {
      title: "Thời gian & Địa điểm",
      key: "datetime",
      render: (_: any, m: MatchItem) => {
        const std = dictionariesById.stadium[m.stadiumId || 0]?.name;
        return (
          <Space direction="vertical" size={2}>
            {m.ballTimeText && (
              <Space size={8}>
                <CalendarOutlined style={{ color: "#8c8c8c" }} />
                <Text type="secondary">{m.ballTimeText}</Text>
              </Space>
            )}
            {std && (
              <Space size={8}>
                <EnvironmentOutlined style={{ color: "#8c8c8c" }} />
                <Text type="secondary">{std}</Text>
              </Space>
            )}
          </Space>
        );
      },
    },
    {
      title: "Giải đấu",
      key: "tournament",
      render: (_: any, m: MatchItem) => {
        const tnm = dictionariesById.tournament[m.tournamentId || 0]?.name;
        return tnm ? <Tag color="blue" style={{ borderRadius: 4, padding: "2px 10px" }}>{tnm}</Tag> : null;
      },
    },
    {
      title: "Thao tác",
      key: "actions",
      align: "right" as const,
      width: 100,
      render: (_: any, m: MatchItem) => (
        <Tooltip title="Chỉnh sửa">
          <Button
            icon={<EditOutlined />}
            onClick={() => openEdit(m.id)}
            style={{ borderRadius: 6 }}
          />
        </Tooltip>
      ),
    },
  ];

  if (loading) return <Loader />;

  return (
    <>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={24} md={8}>
              <Button type="primary" onClick={openCreate}>Tạo mới</Button>
            </Col>
            <Col xs={24} md={16}>
              <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
              <Input
                  allowClear
                  placeholder="ID trận"
                  style={{ minWidth: 120 }}
                  value={idKeyword}
                  onChange={(e) => setIdKeyword(e.target.value)}
                  type="number"
                />
                <Input
                  allowClear
                  placeholder="Tên trận"
                  style={{ minWidth: 180 }}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
                <Select
                  style={{ minWidth: 260 }}
                  placeholder="Giải đấu"
                  allowClear
                  value={selectedTournament}
                  onChange={(v) => { setSelectedTournament(v); }}
                  onDropdownVisibleChange={(open: boolean) => {
                    if (open) {
                      resetTournamentOptions();
                      void loadTournamentOptions(1);
                    }
                  }}
                  onPopupScroll={(e: any) => {
                    const target = e?.target;
                    if (!target) return;
                    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 24) {
                      const canLoadMore = tournaments.length < tourTotal;
                      if (canLoadMore) void loadTournamentOptions(tourPage + 1);
                    }
                  }}
                  options={(tournaments || []).map((t: any) => ({ value: t.id, label: `${t.name}${t.year ? ` (${t.year})` : ''}` }))}
                />
                <Tooltip title="Chỉ trận sắp diễn ra">
                  <span>
                    <Switch checked={showUpcomingDraft} onChange={(v) => setShowUpcomingDraft(v)} /> <Text type="secondary">Upcoming</Text>
                  </span>
                </Tooltip>
                <Button onClick={() => {
                  setKeyword("");
                  setIdKeyword("");
                  setSelectedTournament(undefined);
                  setShowUpcomingDraft(false);
                  setActiveKeyword("");
                  setActiveIdKeyword("");
                  setActiveTournament(undefined);
                  setActiveUpcoming(false);
                  setCurrentPage(1);
                  if (activeYear !== undefined) void loadMatches(String(activeYear));
                }}>Reset</Button>
                <Button type="primary" onClick={onApplyAllFilters}>Tìm kiếm</Button>
              </Space>
            </Col>
          </Row>
          <Tabs
            type="card"
            size="large"
            tabBarGutter={8}
            animated
            tabBarStyle={{ marginBottom: 16 }}
            activeKey={activeYear !== undefined ? String(activeYear) : undefined}
            onChange={(key: string) => setActiveYear(key)}
          >
            {(years || []).map((y) => {
              const key = String(y);
              const list = yearToMatches[key] || [];
              const total = yearToTotal[key] || 0;
              const isActive = String(activeYear) === key;
              return (
                <TabPane
                  key={key}
                  tab={
                    <Space size={6}>
                      <Text strong={isActive}>{key}</Text>
                      <Tag color={isActive ? "geekblue" : "default"}>{total}</Tag>
                    </Space>
                  }
                >
                  <Card bodyStyle={{ padding: 0 }} style={{ marginBottom: 16 }}>
                    <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0" }}>
                      <Typography.Title level={4} style={{ margin: 0 }}>Danh sách trận đấu</Typography.Title>
                    </div>
                    <Table
                      dataSource={list}
                      columns={columns}
                      pagination={false}
                      rowKey="id"
                      loading={false}
                    />
                  </Card>
                  <Row>
                    <Col span={24}>
                      <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                        <Pagination
                          current={currentPage}
                          pageSize={pageSize}
                          total={yearToTotal[key] || 0}
                          showSizeChanger
                          onChange={(p, ps) => {
                            setCurrentPage(p || 1);
                            setPageSize(ps || pageSize);
                            setYearToMatches((prev) => ({ ...prev, [key]: [] }));
                            void loadMatches(key);
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </TabPane>
              );
            })}
          </Tabs>
        </Col>
      </Row>

      <Modal
        width="80vw"
        visible={isShowModal}
        footer={null}
        destroyOnClose
        onCancel={closeModal}
        title={modalQuery?.mode === "edit" ? "Cập nhật Match" : "Tạo mới Match"}
      >
        {isShowModal ? (
          <FormCtrl query={modalQuery} onClose={closeModal} />
        ) : (
          <Loader />
        )}
      </Modal>
    </>
  );
};

export default MatchCtrl;


